import {CommonModule} from '@angular/common';
import {Component, Inject, OnDestroy} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from '@angular/forms';
import {MatAutocompleteModule} from '@angular/material/autocomplete';
import {MatButtonModule} from '@angular/material/button';
import {MatCheckboxModule} from '@angular/material/checkbox';
import {MAT_DIALOG_DATA, MatDialogModule, MatDialogRef} from '@angular/material/dialog';
import {MatFormFieldModule} from '@angular/material/form-field';
import {MatIconModule} from '@angular/material/icon';
import {MatInputModule} from '@angular/material/input';
import {MatSelectModule} from '@angular/material/select';
import {of, Subject} from 'rxjs';
import {catchError, debounceTime, distinctUntilChanged, switchMap, takeUntil} from 'rxjs/operators';
import {CodingSchemaDTO} from '../../../models/CodingSchemaDTO';
import {DiagnosisDTO} from '../../../models/DiagnosisDTO';
import {CodingSchemaService} from '../../../services/coding-schema.service';
import {DiagnosisService} from '../../../services/diagnosis.service';
import {ProcedureCrosswalk, ProcedureCrosswalkService} from '../../../services/procedure-crosswalk.service';

@Component({
  selector: 'app-procedure-crosswalk-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatAutocompleteModule, MatButtonModule, MatCheckboxModule,
    MatDialogModule, MatFormFieldModule, MatIconModule, MatInputModule, MatSelectModule],
  templateUrl: './procedure-crosswalk-dialog.component.html',
  styleUrls: ['./procedure-crosswalk-dialog.component.scss']
})
export class ProcedureCrosswalkDialogComponent implements OnDestroy {
  private static readonly RPL_CODING_SCHEMA_TYPE_ID = 10;
  private static readonly CCSA_CODING_SCHEMA_TYPE_ID = 8;
  private readonly destroyed$ = new Subject<void>();
  private readonly rplSearch$ = new Subject<string>();
  private readonly ccsaSearch$ = new Subject<string>();
  private readonly diagnosisSearch$ = new Subject<string>();

  readonly heading: string;
  readonly form: FormGroup;
  readonly rplControl: FormControl;
  readonly diagnosisControl: FormControl;
  readonly ccsaControl: FormControl;
  rplOptions: CodingSchemaDTO[] = [];
  ccsaOptions: CodingSchemaDTO[] = [];
  diagnosisOptions: DiagnosisDTO[] = [];
  saving = false;
  error = '';

  readonly displayCodingSchema = (value: CodingSchemaDTO | string | null): string => {
    if (!value) {
      return '';
    }
    return typeof value === 'string' ? value : `${value.code}: ${value.shortDescription || ''}`;
  };

  readonly displayDiagnosis = (value: DiagnosisDTO | string | null): string => {
    if (!value) {
      return '';
    }
    return typeof value === 'string' ? value : `${value.code}: ${value.description || ''}`;
  };

  constructor(
    private dialogRef: MatDialogRef<ProcedureCrosswalkDialogComponent>,
    private formBuilder: FormBuilder,
    private crosswalkService: ProcedureCrosswalkService,
    private codingSchemaService: CodingSchemaService,
    private diagnosisService: DiagnosisService,
    @Inject(MAT_DIALOG_DATA) public row: ProcedureCrosswalk | null
  ) {
    this.heading = row?.procedureCrosswalkId ? 'Edit Mapping' : 'Add Mapping';
    this.rplControl = new FormControl(row ? this.codingSchema(row.rplCode, row.rplDescription) : null, Validators.required);
    this.diagnosisControl = new FormControl(row ? this.diagnosis(row.primaryDiagnosisCode, row.diagnosisDescription) : null, Validators.required);
    this.ccsaControl = new FormControl(row?.ccsaCode ? this.codingSchema(row.ccsaCode, row.ccsaDescription) : null);
    this.form = this.formBuilder.group({
      rpl: this.rplControl,
      diagnosis: this.diagnosisControl,
      ccsa: this.ccsaControl,
      minimumAge: [row?.minimumAge ?? null, [Validators.min(0), Validators.pattern(/^\d*$/)]],
      maximumAge: [row?.maximumAge ?? null, [Validators.min(0), Validators.pattern(/^\d*$/)]],
      ageUnit: [row?.ageUnit ?? null],
      notes: [row?.notes ?? '', Validators.maxLength(4000)],
      active: [row?.active ?? true],
      requiresClinicalReview: [row?.requiresClinicalReview ?? false]
    });
    this.configureSearches();
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  searchRpl(value: string): void {
    this.rplSearch$.next(value);
  }

  searchCcsa(value: string): void {
    this.ccsaSearch$.next(value);
  }

  searchDiagnosis(value: string): void {
    this.diagnosisSearch$.next(value);
  }

  clearCcsa(): void {
    this.ccsaControl.reset();
    this.ccsaOptions = [];
  }

  save(): void {
    if (this.saving) {
      return;
    }
    this.error = '';
    const rpl = this.rplControl.value as CodingSchemaDTO;
    const diagnosis = this.diagnosisControl.value as DiagnosisDTO;
    const ccsa = (this.ccsaControl.value || null) as CodingSchemaDTO | null;
    if (this.form.invalid || !this.isCodingSchema(rpl) || !this.isDiagnosis(diagnosis)
      || (ccsa != null && !this.isCodingSchema(ccsa))) {
      this.error = 'Select valid RPL, primary ICD and CCSA values.';
      return;
    }
    const minimumAge = this.form.value.minimumAge;
    const maximumAge = this.form.value.maximumAge;
    let ageUnit = this.form.value.ageUnit;
    if (minimumAge == null && maximumAge == null) {
      ageUnit = null;
    }
    if ((minimumAge != null || maximumAge != null) && !ageUnit) {
      this.error = 'Select an age unit for the age range.';
      return;
    }
    if (minimumAge != null && maximumAge != null && Number(minimumAge) > Number(maximumAge)) {
      this.error = 'Minimum age cannot exceed maximum age.';
      return;
    }
    const mapping: ProcedureCrosswalk = {
      ...(this.row || {} as ProcedureCrosswalk),
      version: this.row?.version ?? 0,
      rplCode: rpl.code.trim().toUpperCase(),
      primaryDiagnosisCode: diagnosis.code.trim().toUpperCase(),
      ccsaCode: ccsa?.code?.trim().toUpperCase() || null,
      minimumAge: minimumAge == null ? null : Number(minimumAge),
      maximumAge: maximumAge == null ? null : Number(maximumAge),
      ageUnit,
      notes: this.form.value.notes,
      active: this.form.value.active,
      requiresClinicalReview: this.form.value.requiresClinicalReview
    };
    this.saving = true;
    this.crosswalkService.save(mapping).subscribe({
      next: () => this.dialogRef.close(true),
      error: response => {
        this.saving = false;
        this.error = response.status === 409
          ? 'This mapping changed or duplicates an existing candidate. Close, reload and retry.'
          : response.status === 403 ? 'Your role does not permit mapping changes.'
            : 'Could not save the mapping. Your changes have been kept.';
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private configureSearches(): void {
    this.rplSearch$.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap(value => this.searchCodingSchemas(value, ProcedureCrosswalkDialogComponent.RPL_CODING_SCHEMA_TYPE_ID)),
      takeUntil(this.destroyed$)
    ).subscribe(options => this.rplOptions = options);
    this.ccsaSearch$.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap(value => this.searchCodingSchemas(value, ProcedureCrosswalkDialogComponent.CCSA_CODING_SCHEMA_TYPE_ID)),
      takeUntil(this.destroyed$)
    ).subscribe(options => this.ccsaOptions = options);
    this.diagnosisSearch$.pipe(
      debounceTime(300), distinctUntilChanged(),
      switchMap(value => value?.trim().length < 2
        ? of([] as DiagnosisDTO[])
        : this.diagnosisService.searchDiagnoses(value.trim()).pipe(
          switchMap(response => of(response.resultList || [])),
          catchError(() => of([] as DiagnosisDTO[])))),
      takeUntil(this.destroyed$)
    ).subscribe(options => this.diagnosisOptions = options);
  }

  private searchCodingSchemas(value: string, typeId: number) {
    if (!value || value.trim().length < 2) {
      return of([] as CodingSchemaDTO[]);
    }
    return this.codingSchemaService.searchCodingSchemas(value.trim(), typeId).pipe(
      switchMap(response => of(response.resultList || [])),
      catchError(() => of([] as CodingSchemaDTO[]))
    );
  }

  private isCodingSchema(value: CodingSchemaDTO): boolean {
    return value != null && typeof value !== 'string' && !!value.code;
  }

  private isDiagnosis(value: DiagnosisDTO): boolean {
    return value != null && typeof value !== 'string' && !!value.code;
  }

  private codingSchema(code: string, description?: string): CodingSchemaDTO {
    return {code, shortDescription: description || ''} as CodingSchemaDTO;
  }

  private diagnosis(code: string, description?: string): DiagnosisDTO {
    return {code, description: description || ''} as DiagnosisDTO;
  }
}
